import { useState } from 'react'
import styles from './ScoopSlider.module.scss'

/**
 * ScoopSlider - A custom slider component with single value and range support
 *
 * @component
 * @param {Object} props - Component props
 * @param {number|number[]} props.value - Current value (number for single, [min, max] array for range)
 * @param {function} props.onChange - Change handler (receives value or [min, max] array)
 * @param {number} [props.min=0] - Minimum value
 * @param {number} [props.max=100] - Maximum value
 * @param {number} [props.step=1] - Step increment
 * @param {string} [props.label] - Label for the slider
 * @param {Array} [props.marks] - Array of mark positions (e.g., [{value: 25}, {value: 50}])
 * @param {boolean} [props.valueLabelDisplay=false] - Whether to show value tooltip
 * @param {boolean} [props.showMinMaxLabels=false] - Whether to show min/max labels at slider ends
 * @param {boolean} [props.disabled=false] - Whether the slider is disabled
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props['aria-labelledby']] - Aria label id
 * @param {...any} props - All other props are passed to the native HTML input element
 *
 * @example
 * // Single value slider
 * <ScoopSlider value={50} onChange={(val) => setValue(val)} />
 *
 * @example
 * // Range slider (two thumbs)
 * <ScoopSlider value={[20, 80]} onChange={(range) => setRange(range)} />
 *
 * @example
 * // Slider with min/max labels
 * <ScoopSlider value={50} onChange={(val) => setValue(val)} showMinMaxLabels />
 */
export const ScoopSlider = ({
    value = 0,
    onChange,
    min = 0,
    max = 100,
    step = 1,
    label,
    marks = [],
    valueLabelDisplay = false,
    showMinMaxLabels = false,
    disabled = false,
    className = '',
    'aria-labelledby': ariaLabelledBy,
    ...props
}) => {
    const [showTooltip, setShowTooltip] = useState(false)
    const [activeThumb, setActiveThumb] = useState(null)

    // Detect if range mode (value is array)
    const isRange = Array.isArray(value)
    const rangeValue = isRange ? value : [value, value]
    const [minValue, maxValue] = rangeValue

    // Single slider change handler
    const handleSingleChange = (e) => {
        const newValue = parseFloat(e.target.value)
        onChange?.(newValue)
    }

    // Range slider change handlers
    const handleMinChange = (e) => {
        const newMin = parseFloat(e.target.value)
        // Prevent min from exceeding max
        if (newMin <= maxValue) {
            onChange?.([newMin, maxValue])
        }
    }

    const handleMaxChange = (e) => {
        const newMax = parseFloat(e.target.value)
        // Prevent max from going below min
        if (newMax >= minValue) {
            onChange?.([minValue, newMax])
        }
    }

    // Calculate percentages for CSS custom properties
    const minPercentage = ((minValue - min) / (max - min)) * 100
    const maxPercentage = ((maxValue - min) / (max - min)) * 100
    const singlePercentage = isRange ? 0 : ((value - min) / (max - min)) * 100

    const containerClassName = [styles.container, disabled && styles.disabled, className]
        .filter(Boolean)
        .join(' ')

    // CSS custom properties for dynamic values (passed to SCSS)
    // Note: Pass as numbers (0-100), not percentages, for valid CSS calc() math
    const cssVars = {
        '--slider-percentage': singlePercentage,
        '--slider-min-percentage': minPercentage,
        '--slider-max-percentage': maxPercentage,
    }

    // Single slider mode
    if (!isRange) {
        return (
            <div className={containerClassName} style={cssVars}>
                {label && <label className={styles.label}>{label}</label>}
                <div className={styles.sliderRow}>
                    {showMinMaxLabels && <span className={styles.minMaxLabel}>{min}</span>}
                    <div
                        className={styles.sliderWrapper}
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        {/* Track background */}
                        <div className={styles.singleTrack} />

                        <input
                            type="range"
                            className={styles.slider}
                            value={value}
                            min={min}
                            max={max}
                            step={step}
                            disabled={disabled}
                            onChange={handleSingleChange}
                            onFocus={() => setShowTooltip(true)}
                            onBlur={() => setShowTooltip(false)}
                            aria-labelledby={ariaLabelledBy}
                            {...props}
                        />

                        {/* Value tooltip */}
                        {valueLabelDisplay && showTooltip && (
                            <div className={styles.valueLabel}>{value}</div>
                        )}

                        {/* Marks */}
                        {marks.length > 0 && (
                            <div className={styles.marks}>
                                {marks.map((mark, index) => {
                                    const markPercentage = ((mark.value - min) / (max - min)) * 100
                                    const isPassed = mark.value <= value
                                    return (
                                        <div
                                            key={index}
                                            className={`${styles.mark} ${isPassed ? styles.markPassed : ''}`}
                                            style={{ '--mark-position': markPercentage }}
                                        />
                                    )
                                })}
                            </div>
                        )}
                    </div>
                    {showMinMaxLabels && <span className={styles.minMaxLabel}>{max}</span>}
                </div>
            </div>
        )
    }

    // Range slider mode (two thumbs)
    return (
        <div className={containerClassName} style={cssVars}>
            {label && <label className={styles.label}>{label}</label>}
            <div className={styles.sliderRow}>
                {showMinMaxLabels && <span className={styles.minMaxLabel}>{min}</span>}
                <div
                    className={styles.sliderWrapper}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    {/* Track background */}
                    <div className={styles.rangeTrack} />

                    {/* Min thumb input */}
                    <input
                        type="range"
                        className={`${styles.slider} ${styles.rangeSlider}`}
                        value={minValue}
                        min={min}
                        max={max}
                        step={step}
                        disabled={disabled}
                        onChange={handleMinChange}
                        onFocus={() => {
                            setShowTooltip(true)
                            setActiveThumb('min')
                        }}
                        onBlur={() => {
                            setShowTooltip(false)
                            setActiveThumb(null)
                        }}
                        onMouseDown={() => setActiveThumb('min')}
                        aria-label="Minimum value"
                        {...props}
                    />

                    {/* Max thumb input */}
                    <input
                        type="range"
                        className={`${styles.slider} ${styles.rangeSlider}`}
                        value={maxValue}
                        min={min}
                        max={max}
                        step={step}
                        disabled={disabled}
                        onChange={handleMaxChange}
                        onFocus={() => {
                            setShowTooltip(true)
                            setActiveThumb('max')
                        }}
                        onBlur={() => {
                            setShowTooltip(false)
                            setActiveThumb(null)
                        }}
                        onMouseDown={() => setActiveThumb('max')}
                        aria-label="Maximum value"
                        {...props}
                    />

                    {/* Value tooltips for range */}
                    {valueLabelDisplay && showTooltip && (
                        <>
                            {activeThumb === 'min' && (
                                <div className={`${styles.valueLabel} ${styles.valueLabelMin}`}>
                                    {minValue}
                                </div>
                            )}
                            {activeThumb === 'max' && (
                                <div className={`${styles.valueLabel} ${styles.valueLabelMax}`}>
                                    {maxValue}
                                </div>
                            )}
                        </>
                    )}

                    {/* Marks */}
                    {marks.length > 0 && (
                        <div className={styles.marks}>
                            {marks.map((mark, index) => {
                                const markPercentage = ((mark.value - min) / (max - min)) * 100
                                const isPassed = mark.value >= minValue && mark.value <= maxValue
                                return (
                                    <div
                                        key={index}
                                        className={`${styles.mark} ${isPassed ? styles.markPassed : ''}`}
                                        style={{ '--mark-position': markPercentage }}
                                    />
                                )
                            })}
                        </div>
                    )}
                </div>
                {showMinMaxLabels && <span className={styles.minMaxLabel}>{max}</span>}
            </div>
        </div>
    )
}

export default ScoopSlider
